import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { PlusCircle, ListChecks, CreditCard, Palette, LogOut, Menu, Landmark, Users, UsersRound, BarChart3, FileText, Plane } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

import { ROLE_LABELS } from "@/lib/i18n";
import { InstallApp } from "@/components/InstallApp";
import { ChangePassword } from "@/components/ChangePassword";
import { useManualReview } from "@/components/ManualReview";
import { useSignAlertsCount } from "@/components/SignAlerts";

const NAV = [
  { to: "/panel/ventas", tab: "ventas", label: "Panel de ventas", icon: BarChart3, id: "sidebar-nav-ventas" },
  { to: "/panel/nuevo", tab: "nuevo", label: "Nuevo cobro", icon: PlusCircle, id: "sidebar-nav-nuevo-cobro" },
  { to: "/panel/cobros", tab: "cobros", label: "Reservaciones y cobros", icon: ListChecks, id: "sidebar-nav-reservaciones" },
  { to: "/panel/procesadores", tab: "procesadores", label: "Procesadores de pago", icon: CreditCard, id: "sidebar-nav-procesadores" },
  { to: "/panel/plantillas", tab: "plantillas", label: "Plantillas", icon: Palette, id: "sidebar-nav-configuracion" },
  { to: "/panel/usuarios", role: "admin", label: "Usuarios y accesos", icon: Users, id: "sidebar-nav-usuarios" },
  { to: "/panel/equipo", role: "manager", label: "Mi equipo", icon: UsersRound, id: "sidebar-nav-equipo" },
  { to: "/panel/docs", roles: ["admin", "manager", "agent"], label: "Certificados", icon: FileText, id: "sidebar-nav-docs" },
  { to: "/panel/aerolineas", role: "admin", label: "Aerolíneas", icon: Plane, id: "sidebar-nav-aerolineas" },
];

export const navFor = (user) => NAV.filter((n) => (n.tab ? user?.permissions?.includes(n.tab) : n.roles ? n.roles.includes(user?.role) : user?.role === n.role));

const SidebarBody = ({ onNavigate, reviewCount = 0 }) => {
  const { user, logout } = useAuth();
  const signAlerts = useSignAlertsCount(user);
  return (
    <div className="flex h-full flex-col bg-[#0D1117] text-slate-200">
      <div className="flex items-center gap-3 px-6 py-7">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#2F5BFF] text-white"><Landmark size={20} /></div>
        <div>
          <p className="font-heading text-lg font-semibold leading-none text-white">Cobros</p>
          <p className="mt-1 text-xs text-slate-400">Reservaciones</p>
        </div>
      </div>
      <nav className="flex-1 space-y-1 px-3">
        {navFor(user).map(({ to, label, icon: Icon, id }) => (
          <NavLink key={to} to={to} data-testid={id} onClick={onNavigate}
            className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors duration-200 ${isActive ? "bg-[#2F5BFF] font-semibold text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
            <Icon size={18} /> {label}
            {to === "/panel/cobros" && reviewCount > 0 && (
              <span className="ml-auto rounded-full bg-amber-400 px-2 py-0.5 text-[11px] font-bold text-slate-900" data-testid="manual-review-badge">{reviewCount}</span>
            )}
            {to === "/panel/docs" && signAlerts > 0 && (
              <span className="ml-auto rounded-full bg-rose-500 px-2 py-0.5 text-[11px] font-bold text-white" data-testid="sign-alerts-badge">{signAlerts}</span>
            )}
          </NavLink>
        ))}
      </nav>
      <div className="border-t border-white/10 p-4">
        <p className="truncate text-xs text-slate-400" data-testid="sidebar-user-email">{user?.email}</p>
        <p className="mt-0.5 text-[11px] font-semibold uppercase tracking-wider text-[#7C9BFF]" data-testid="sidebar-user-role">{ROLE_LABELS[user?.role]}</p>
        <button data-testid="sidebar-logout-button" onClick={logout}
          className="mt-3 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-sm text-slate-300 transition-colors hover:bg-white/5 hover:text-white">
          <LogOut size={16} /> Cerrar sesión
        </button>
        {user?.role !== "admin" && <ChangePassword />}
        <InstallApp />
      </div>
    </div>
  );
};

export const AdminLayout = () => {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const review = useManualReview(user?.role === "admin", true);
  return (
    <div className="admin-shell min-h-screen bg-[#F5F6F8]">
      <aside className="fixed inset-y-0 left-0 hidden w-64 lg:block"><SidebarBody reviewCount={review.length} /></aside>
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-slate-200 bg-white/80 px-4 py-3 backdrop-blur-md lg:hidden">
        <p className="font-heading font-semibold">Cobros</p>
        <button data-testid="mobile-menu-button" onClick={() => setOpen(true)} className="rounded-md p-2 hover:bg-slate-100"><Menu size={20} /></button>
      </header>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-64 border-0 p-0">
          <SheetTitle className="sr-only">Menú</SheetTitle>
          <SidebarBody reviewCount={review.length} onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <main className="lg:pl-64">
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-8 lg:py-10"><Outlet /></div>
      </main>
    </div>
  );
};

export const PageHeader = ({ title, subtitle, actions }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <h1 className="font-heading text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">{title}</h1>
      {subtitle && <p className="mt-2 max-w-2xl text-sm text-slate-500 sm:text-base">{subtitle}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);
